import { useEffect } from 'react'

/**
 * Executes a callback when the page is restored from the browser's page cache.
 *
 * When a user navigates away and returns via the back/forward button, browsers
 * may restore the page from cache instead of reloading. In this case, React's
 * useMount/useEffect won't re-run because the JavaScript state is preserved.
 * This hook fills that gap.
 *
 * @param onRestore - Callback to execute when page is restored from cache
 *
 * @example
 * useOnPageRestore(() => {
 *   checkForStaleSession()
 * })
 */
const useOnPageRestore = (onRestore: () => void) => {
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        onRestore()
      }
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [onRestore])
}

export default useOnPageRestore
